import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Skeleton,
} from "@mui/material";
import axios from "axios";
import {
  CurrencyNumberInput,
  FormLabel,
  ModalError,
  ModalSuccess,
  ModalWarning,
  PrimaryButton,
  SecondaryButton,
  SectionLabel,
  TextInput,
} from "components";
import { useEffect, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { getErrors } from "utils";

const ModalFormOwnershipConflict = ({
  open,
  onClose,
  preload,
  getDataTable,
}) => {
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [payload, setPayload] = useState({
    song_title: "",
    publisher_percentages: [],
  });
  const handleChangeOwnershipPercentage = (
    value,
    publisher_id,
    composer_id
  ) => {
    setPayload(prev => ({
      ...prev,
      publisher_percentages: prev.publisher_percentages.map(publisher =>
        publisher.publisher_id === publisher_id
          ? {
              ...publisher,
              ownerships: publisher?.ownerships.map(composer =>
                composer?.composer_id === composer_id
                  ? { ...composer, ownership_percentage: value }
                  : composer
              ),
            }
          : publisher
      ),
    }));
  };

  const getDetail = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/songs/song-conflict/${preload}`,
        { headers }
      );

      const { data } = res?.data;
      setPayload(data);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const handleSubmit = async () => {
    const modifiedPayload = {
      song_composers: payload?.publisher_percentages
        ?.filter(publisher => publisher?.is_own_publisher)
        .flatMap(publisher =>
          publisher?.ownerships?.map(
            ({ composer_id, ownership_percentage, publisher_id }) => ({
              composer_id,
              ownership_percentage,
              publisher_id,
            })
          )
        ),
    };
    const totalOwnership = payload?.publisher_percentages?.reduce(
      (sum, publisher) => {
        return (
          sum +
          publisher.ownerships.reduce((innerSum, owner) => {
            return innerSum + Number(owner.ownership_percentage || 0);
          }, 0)
        );
      },
      0
    );

    const hasFullPercentage = totalOwnership > 100;

    if (hasFullPercentage) {
      const res = await ModalWarning(
        "The total ownership share of this song is more than 100%. Do you want to proceed?"
      );
      if (!res.isConfirmed) {
        return;
      }
    }
    try {
      setLoadingButton(true);
      await axios.put(
        `${hardBaseUrl}/songs/song-conflict/${preload}`,
        modifiedPayload,
        { headers }
      );
      onClose();
      ModalSuccess("Ownership conflict updated successfully").then(() =>
        getDataTable()
      );
    } catch (error) {
      ModalError(getErrors(error?.response));
      onClose();
    } finally {
      setLoadingButton(false);
    }
  };
  useEffect(() => {
    getDetail();
  }, []);
  return (
    <Dialog fullWidth open={open} onClose={onClose} sx={{ zIndex: 2 }}>
      <DialogTitle>Edit Ownership Conflict</DialogTitle>
      <DialogContent dividers>
        {loadingPage ? (
          <Grid container direction="column" rowSpacing={2}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Grid item key={index}>
                <Box mb="8px">
                  <Skeleton variant="rounded" width="40%" height={25} />
                </Box>
                <Skeleton variant="rounded" width="100%" height={30} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container direction="column" rowSpacing={2}>
            <Grid item>
              <FormLabel label="Song Title" />
              <TextInput value={payload?.song_title} disabled />
            </Grid>
            {payload?.publisher_percentages?.map(
              ({ is_own_publisher, ownerships, publisher_name }, index) => {
                return (
                  <Grid item key={index}>
                    <SectionLabel title={publisher_name} />
                    <Grid container direction="column" rowSpacing={1}>
                      {ownerships?.length > 0 &&
                        ownerships?.map(
                          (
                            {
                              composer_id,
                              composer_name,
                              ownership_percentage,
                              publisher_id,
                            },
                            idx
                          ) => (
                            <Grid item key={idx}>
                              <FormLabel
                                label={`${composer_name}`}
                                required={is_own_publisher}
                              />
                              <CurrencyNumberInput
                                endAdornment="%"
                                value={ownership_percentage}
                                onChange={val =>
                                  handleChangeOwnershipPercentage(
                                    Number(val),
                                    publisher_id,
                                    composer_id
                                  )
                                }
                                disabled={!is_own_publisher || loadingButton}
                              />
                            </Grid>
                          )
                        )}
                    </Grid>
                  </Grid>
                );
              }
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <SecondaryButton
          label="Cancel"
          disabled={loadingButton}
          onClick={onClose}
        />
        <PrimaryButton
          label={loadingButton ? "Saving" : "Save"}
          loading={loadingButton}
          disabled={loadingButton || loadingPage}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};
export default ModalFormOwnershipConflict;
